import React, { Component } from 'react';
import { SectionList, View, ScrollView, Text } from 'react-native';
import PropTypes from 'prop-types';
import {
    Background,
    LabeledSwitch,
    SectionListItem,
    OptionsSelectBox,
    PinInput,
    ActivityIndicator,
    Hr
} from 'LocalGluons';
import storeActions from 'Store/storeActions';
import { t } from 'Locale';
import { withStore } from 'Store';
import { ChangePinModal } from './components/ChangePinModal';
import { withAppErrorHandler } from '../../App/AppErrorHandlerContext/AppErrorHandlerContext';

import defaultStyles from './Parental.jss';

class ParentalPage extends Component {
    // eslint-disable-next-line react/sort-comp
    autoLockingItems = [
        {
            id: 'dontAutoLock',
            label: 'SETTINGS.PARENTAL_CONTROL_AUTO_DONT',
            value: 0
        },
        {
            id: 'autoLock5',
            label: 'SETTINGS.PARENTAL_CONTROL_AUTO_5',
            value: 5
        },
        {
            id: 'autoLock30',
            label: 'SETTINGS.PARENTAL_CONTROL_AUTO_30',
            value: 30
        },
        {
            id: 'autoLock120',
            label: 'SETTINGS.PARENTAL_CONTROL_AUTO_2H',
            value: 120
        }
    ];

    static getDerivedStateFromProps(props, state) {
        const { navigation, route } = props;
        const params = route?.params || undefined;
        if (params && params.pin && !state.pinProvided) {
            return {
                ...state,
                pinProvided: true
            };
        }
        return null;
    }

    constructor(props) {
        super(props);

        const { navigation, route } = props;
        const params = route?.params || undefined;
        const pinProvided = params && !!params.pin;

        this.state = {
            pinProvided,
            changePin: false,
            contentBlockingPickerSelected: '',
            autoLockingPickerSelected: '',
            loading: pinProvided, // if a pin is provided, it needs to load config
            pinErrorMessage: ''
        };
    }

    async componentDidMount() {
        const { navigation } = this.props;
        this.navWillFocusListener = navigation.addListener('focus', () => {
            const { pinProvided } = this.state;
            if (pinProvided) this.setState({ pinProvided: false });
        });
        const { pinProvided } = this.state;
        if (pinProvided) {
            this.adjustState();
        } else {
            this.setState({ loading: false });
        }
    }

    componentWillUnmount() {
        if (this.navWillFocusListener) this.navWillFocusListener();
    }

    onChangeContentBlockingPicker = async (rating) => {
        const { mCoreConfig } = this.props;
        const blockingItem = mCoreConfig.allRatings.find((item) => item.rating === rating);
        if (blockingItem) {
            try {
                await this.setParentalControls({
                    rating: blockingItem.rating
                });
                this.setState({ contentBlockingPickerSelected: rating });
            } catch (error) {
                console.log(error);
            }
        }
    };

    async setParentalControls(config) {
        const { setError } = this.props;
        try {
            const { appManager, parentalLockConfig, setParentalLockConfig } = this.props;
            const newConfig = { ...parentalLockConfig, ...config };
            // If rating is set on some value, make sure lockAdultContent is set to true
            if (newConfig.rating > -1) {
                newConfig.lockAdultContent = true;
            }
            const requestParams = { ...newConfig };
            // This property is only for internal use
            delete requestParams.unlocked;
            await appManager.getApi().setParentalLocking(requestParams);

            setParentalLockConfig(newConfig);
        } catch (error) {
            setError({ error, screenId: 'parentalSettings' });
        }
    }

    adjustState() {
        const { parentalLockConfig, mCoreConfig, deviceStorageState } = this.props;
        const blockingItem = mCoreConfig.allRatings.find(
            (item) => item.rating === parentalLockConfig.rating
        );
        const autoLockingItem = this.autoLockingItems.find(
            (item) => item.value === deviceStorageState.autoParentalLockInterval
        );

        this.setState({
            contentBlockingPickerSelected: blockingItem?.rating || mCoreConfig.rating,
            autoLockingPickerSelected: autoLockingItem?.id,
            loading: false
        });
    }

    onChangeAutoLockingPicker = (id) => {
        const autoLockingItem = this.autoLockingItems.find((item) => item.id === id);
        if (autoLockingItem) {
            const { deviceStorageDispatch } = this.props;
            deviceStorageDispatch({
                type: storeActions.setAutoParentalLockInterval,
                payload: autoLockingItem.value
            });
            this.setState({ autoLockingPickerSelected: id });
        }
    };

    onAdultContentBlockedValueChange = async (value) => {
        try {
            await this.setParentalControls({ lockAdultContent: value });
        } catch (error) {
            console.log(error);
        }
    };

    onAdultChannelsHiddenValueChange = async (value) => {
        try {
            await this.setParentalControls({ hideAdultChannels: value });
        } catch (error) {
            console.log(error);
        }
    };

    onUnknownRatingsBlockedValueChange = async (value) => {
        try {
            await this.setParentalControls({ lockUnratedContent: value });
        } catch (error) {
            console.log(error);
        }
    };

    onParentalControlEnabledValueChange = async (value) => {
        try {
            await this.setParentalControls({ enabled: value });
        } catch (error) {
            console.log(error);
        }
    };

    onPinSuccess = () => {
        this.setState({
            pinProvided: true,
            loading: true
        });
        this.adjustState();
    };

    onPinFailed = (message) => {
        this.setState({
            pinErrorMessage: message
        });
    };

    onChangePinPress = () => {
        this.setState({
            changePin: true
        });
    };

    onForgotPinPress = () => {
        const { changePin } = this.state;
        if (changePin) {
            this.closeChangePinPress();
        }
        const { navigation } = this.props;
        navigation.navigate('ResetPin', {
            nextRoute: 'SettingsParental',
            nextRouteParams: {}
        });
    };

    closeChangePinPress = () => {
        this.setState({
            changePin: false
        });
    };

    pinValidateFunction = async (pin) => {
        const { appManager } = this.props;
        const result = await appManager.api.validatePin({
            pin
        });

        if (!result) this.setState({ pinErrorMessage: '' });

        return result ? null : { errorMessage: t('ERROR.WRONG_PIN') };
    };

    cleanErrorMessage = () => {
        this.setState({ pinErrorMessage: '' });
    };

    getSections() {
        const { contentBlockingPickerSelected, autoLockingPickerSelected } = this.state;

        const { parentalLockConfig, mCoreConfig } = this.props;
        const { lockAdultContent, hideAdultChannels, lockUnratedContent } = parentalLockConfig;

        return [
            {
                title: t('SETTINGS.PARENTAL_CONTROL_ADULT_BLOCKING').toUpperCase(),
                data: [
                    {
                        component: (
                            <LabeledSwitch
                                testID="block_adult_content_checkbox"
                                key="SETTINGS.PARENTAL_CONTROL_ADULT_BLOCK"
                                label={t('SETTINGS.PARENTAL_CONTROL_ADULT_BLOCK')}
                                value={lockAdultContent}
                                onValueChange={this.onAdultContentBlockedValueChange}
                            />
                        )
                    },
                    {
                        component: (
                            <>
                                <LabeledSwitch
                                    testID="hide_adult_content_checkbox"
                                    key="SETTINGS.PARENTAL_CONTROL_ADULT_HIDE"
                                    label={t('SETTINGS.PARENTAL_CONTROL_ADULT_HIDE')}
                                    value={hideAdultChannels}
                                    onValueChange={this.onAdultChannelsHiddenValueChange}
                                />
                                <Hr />
                            </>
                        )
                    }
                ]
            },
            {
                data: [
                    {
                        component: (
                            <OptionsSelectBox
                                key="SETTINGS.PARENTAL_CONTROL_RATING_BLOCK"
                                heading={t('SETTINGS.PARENTAL_CONTROL_RATING_BLOCK').toUpperCase()}
                                onChange={this.onChangeContentBlockingPicker}
                                items={mCoreConfig.allRatings.map(({ rating, name }) =>
                                    // We must use rating as id, it should be unique
                                    ({
                                        id: rating,
                                        label: name,
                                        value: rating
                                    })
                                )}
                                selected={contentBlockingPickerSelected}
                            />
                        )
                    }
                ]
            },
            {
                data: [
                    {
                        component: (
                            <>
                                <LabeledSwitch
                                    testID="hide_unknown_rating_checkbox"
                                    key="SETTINGS.PARENTAL_CONTROL_RATING_UNKNOWN"
                                    label={t('SETTINGS.PARENTAL_CONTROL_RATING_UNKNOWN')}
                                    value={lockUnratedContent}
                                    onValueChange={this.onUnknownRatingsBlockedValueChange}
                                />
                                <Hr />
                            </>
                        )
                    }
                ]
            },
            {
                data: [
                    {
                        component: [
                            <OptionsSelectBox
                                key="SETTINGS.PARENTAL_CONTROL_AUTO_LOCK"
                                heading={t('SETTINGS.PARENTAL_CONTROL_AUTO_LOCK').toUpperCase()}
                                onChange={this.onChangeAutoLockingPicker}
                                items={this.autoLockingItems.map((item) => ({
                                    id: item.id,
                                    label: t(item.label)
                                }))}
                                selected={autoLockingPickerSelected}
                            />,
                            <Text
                                key="SETTINGS.PARENTAL_CONTROL_AUTO_INFO"
                                style={defaultStyles.note}
                                numberOfLines={2}
                            >
                                {t('SETTINGS.PARENTAL_CONTROL_AUTO_INFO')}
                            </Text>
                        ]
                    }
                ]
            }
        ];
    }

    render() {
        const { parentalLockConfig } = this.props;
        const { enabled } = parentalLockConfig;
        const { changePin, loading, pinProvided, pinErrorMessage } = this.state;

        if (!pinProvided) {
            return (
                <Background>
                    <PinInput
                        header={t('SETTINGS.PARENTAL_CONTROL_ENTER_PARENTAL_PIN')}
                        message={t('SETTINGS.PARENTAL_CONTROL_ENTER_PARENTAL_PIN_INFO')}
                        onPinSuccess={this.onPinSuccess}
                        onForgotPinPressed={this.onForgotPinPress}
                        pinValidateFunction={this.pinValidateFunction}
                        onPinFailed={this.onPinFailed}
                        invalidPinMessage={pinErrorMessage}
                        cleanErrorMessage={this.cleanErrorMessage}
                    />
                </Background>
            );
        }

        if (loading) {
            return (
                <Background>
                    <ActivityIndicator />
                </Background>
            );
        }

        return (
            <Background>
                <ScrollView
                    style={defaultStyles.container}
                    bounces={enabled}
                    contentInset={{ bottom: 50 }}
                >
                    <LabeledSwitch
                        testID="enable_parental_checkbox"
                        label={t('SETTINGS.PARENTAL_CONTROL_ENABLE')}
                        value={enabled}
                        onValueChange={this.onParentalControlEnabledValueChange}
                    />
                    <Hr />
                    <SectionListItem
                        title={t('SETTINGS.PARENTAL_CONTROL_CHNG_PIN')}
                        onPress={this.onChangePinPress}
                    />
                    {enabled && (
                        <View style={defaultStyles.content}>
                            <SectionList
                                renderItem={({ item }) => item.component}
                                renderSectionHeader={({ section: { title } }) =>
                                    title && <Text style={defaultStyles.sectionTitle}>{title}</Text>
                                }
                                sections={this.getSections()}
                                keyExtractor={(item, index) => item + index}
                            />
                        </View>
                    )}
                </ScrollView>
                {changePin && (
                    <ChangePinModal
                        modalVisible={changePin}
                        onCancel={this.closeChangePinPress}
                        onForgotPinPressed={this.onForgotPinPress}
                        pinValidateFunction={this.pinValidateFunction}
                    />
                )}
            </Background>
        );
    }
}

const ParentalPageWithStore = withStore(ParentalPage);

export default withAppErrorHandler(ParentalPageWithStore);

ParentalPage.propTypes = {
    navigation: PropTypes.shape({
        navigate: PropTypes.func.isRequired
    }).isRequired,
    appManager: PropTypes.shape({}).isRequired,
    deviceStorageDispatch: PropTypes.func.isRequired,
    deviceStorageState: PropTypes.shape({}).isRequired,
    mCoreConfig: PropTypes.shape({}).isRequired,
    parentalLockConfig: PropTypes.shape({
        enabled: PropTypes.bool.isRequired,
        rating: PropTypes.string.isRequired,
        lockUnratedContent: PropTypes.bool.isRequired,
        hideAdultChannels: PropTypes.bool.isRequired,
        lockAdultContent: PropTypes.bool.isRequired,
        unlocked: PropTypes.bool.isRequired
    }).isRequired,
    setParentalLockConfig: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired
};
